import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import DescriptionText from 'components/UI/V3/DescriptionText/DescriptionText';
import { SelectionBar } from 'components/UI/V3/SelectionBar';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import Spinner from 'components/UI/Spinner';

import { useApiClientWrapper, useMarketplace, useTimeZone } from 'hooks';
import { RootState } from 'store';
import {
    fetchVaccineApprovalThunk,
    fetchVaccineApprovalsThunk,
    VaccineApprovalsState
} from '@spike/vaccine-approvals-action';
import { VaccineApproval, VaccineApprovalDetail, VaccineApprovalDetailMode } from 'model/VaccineApproval';

import VaccineApprovalsTable from './Table/VaccineApprovalsTable';
import VaccineApprovalDetailModal from './Detail/VaccineApprovalDetailModal';

import styles from './VaccineApprovals.module.scss';

const TAB_OPTIONS = [
    { id: 'pending', name: 'Pending' },
    { id: 'approved', name: 'Approved' },
    { id: 'declined', name: 'Declined' },
    { id: 'all', name: 'All' }
] as const;

type VaccineApprovalTab = (typeof TAB_OPTIONS)[number]['id'];

interface DetailModalState {
    item: VaccineApproval;
    mode: VaccineApprovalDetailMode;
    detail: VaccineApprovalDetail | null;
    loading: boolean;
}

export const VaccineApprovals: FunctionComponent = () => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const timeZone = useTimeZone();
    const location = useLocation();
    const history = useHistory();

    const vaccineApprovalsSlice = useSelector<RootState, VaccineApprovalsState>(state => state.vaccineApprovals);

    const [selectedTab, setSelectedTab] = useState<VaccineApprovalTab>('pending');
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [initialLoading, setInitialLoading] = useState(true);
    const [detailState, setDetailState] = useState<DetailModalState | null>(null);
    const openedFromQueryRef = useRef<number | null>(null);

    const items = vaccineApprovalsSlice.requests ?? [];
    const pagination = vaccineApprovalsSlice.pagination;
    const loading = vaccineApprovalsSlice.loading;
    const totalPages = pagination?.totalPages ?? Math.max(1, Math.ceil((pagination?.totalRecords ?? 0) / size));

    useEffect(() => {
        const search = searchText.trim() || undefined;
        const status = selectedTab;

        const filterSignature = [search || '', status].join('|');
        const lastFilterRef =
            (VaccineApprovals as any)._lastFilterRef || ((VaccineApprovals as any)._lastFilterRef = { current: '' });

        if (lastFilterRef.current !== filterSignature) {
            lastFilterRef.current = filterSignature;
            if (page !== 1) {
                setPage(1);
                return;
            }
        }

        let cancelled = false;

        const run = async () => {
            try {
                await (dispatch as any)(
                    fetchVaccineApprovalsThunk(apiClientWrapper, {
                        status,
                        search,
                        page,
                        size
                    })
                );
            } catch {
                // reducer handles error state
            } finally {
                if (!cancelled) {
                    setInitialLoading(false);
                }
            }
        };

        void run();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, marketplace.id, searchText, selectedTab, page, size]);

    const handleTabChange = (tab: string) => {
        setSelectedTab(tab as VaccineApprovalTab);
        setPage(1);
    };

    const handleSearchChange = (text: string) => {
        setSearchText(text);
        setPage(1);
    };

    const handleOpenDetail = async (item: VaccineApproval, mode: VaccineApprovalDetailMode) => {
        setDetailState({
            item,
            mode,
            detail: null,
            loading: true
        });

        try {
            const detail = await (dispatch as any)(fetchVaccineApprovalThunk(apiClientWrapper as any, item.id));
            setDetailState(prev =>
                prev && prev.item.id === item.id
                    ? {
                          ...prev,
                          detail: detail || null,
                          mode: detail?.status === 'pending' ? 'review' : mode,
                          loading: false
                      }
                    : prev
            );
        } catch {
            setDetailState(prev =>
                prev && prev.item.id === item.id
                    ? {
                          ...prev,
                          loading: false
                      }
                    : prev
            );
        }
    };

    const openDetailById = async (vaccineApprovalId: number) => {
        try {
            const detail = (await (dispatch as any)(
                fetchVaccineApprovalThunk(apiClientWrapper as any, vaccineApprovalId)
            )) as VaccineApprovalDetail | undefined;

            if (!detail) {
                return;
            }

            const mode: VaccineApprovalDetailMode = detail.status === 'pending' ? 'review' : 'readonly';
            if (detail.status === 'pending' || detail.status === 'approved' || detail.status === 'declined') {
                setSelectedTab(detail.status);
            } else {
                setSelectedTab('all');
            }

            setDetailState({
                item: detail,
                detail,
                mode,
                loading: false
            });
        } catch {
            // reducer handles error
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const vaccineApprovalIdParam = params.get('vaccineApprovalId');
        if (!vaccineApprovalIdParam) {
            return;
        }

        const vaccineApprovalId = Number(vaccineApprovalIdParam);
        if (!Number.isFinite(vaccineApprovalId) || vaccineApprovalId <= 0) {
            return;
        }

        if (openedFromQueryRef.current === vaccineApprovalId) {
            return;
        }
        openedFromQueryRef.current = vaccineApprovalId;

        params.delete('vaccineApprovalId');
        const search = params.toString();
        history.replace({
            pathname: location.pathname,
            search: search ? `?${search}` : '',
            hash: location.hash || '#vaccine-approvals'
        });

        void openDetailById(vaccineApprovalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const handleCloseDetail = () => {
        setDetailState(null);
        openedFromQueryRef.current = null;
    };

    const refreshList = async () => {
        try {
            await (dispatch as any)(
                fetchVaccineApprovalsThunk(apiClientWrapper, {
                    status: selectedTab,
                    search: searchText.trim() || undefined,
                    page,
                    size
                })
            );
        } catch {
            // reducer handles error state
        }
    };

    if (initialLoading) {
        return (
            <div className={styles.loadingContainer}>
                <Spinner />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <div className={styles.headWrapper}>
                <DefaultHeader
                    title={<span className={styles.pageTitle}>Vaccine Approvals</span>}
                    displayButton={false}
                    className={styles.headerContainer}
                />

                <DescriptionText
                    text="All vaccine approvals live here. Review requests that are pending, approved, or declined in one place."
                    className={styles.description}
                />

                <SelectionBar
                    options={[...TAB_OPTIONS]}
                    selectedId={selectedTab}
                    onSelect={handleTabChange}
                    className={styles.selectionBar}
                />

                <SearchBar
                    searchText={searchText}
                    onChangeSearchText={handleSearchChange}
                    placeholder="Search by client name or pet name"
                    className={styles.searchBarContainer}
                />
            </div>

            <div className={styles.tableContainer}>
                {loading && !initialLoading && !detailState ? (
                    <div className={styles.loadingContainer}>
                        <Spinner />
                    </div>
                ) : (
                    <VaccineApprovalsTable
                        items={items}
                        timeZone={timeZone}
                        selectedTab={selectedTab}
                        pagination={{
                            page,
                            totalPages,
                            size
                        }}
                        onPageChange={setPage}
                        onSizeChange={newSize => {
                            setSize(newSize);
                            setPage(1);
                        }}
                        onOpenDetail={handleOpenDetail}
                    />
                )}
            </div>

            {detailState && (
                <VaccineApprovalDetailModal
                    item={detailState.item}
                    detail={detailState.detail}
                    loading={detailState.loading}
                    mode={detailState.mode}
                    onClose={handleCloseDetail}
                    onReviewed={refreshList}
                />
            )}
        </div>
    );
};

export default VaccineApprovals;
